import { Box, CircularProgress } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { useFormik } from "formik"
import { useAppDispatch, useAppSelector } from "hooks"
import { useState } from "react"
import userGroupService from "services/userGroupService"
import { addUserGroup } from "slices/userGroupSlice"
import type { ICreateUserGroup } from "types/userGroupTypes"
import * as Yup from "yup"

export function CreateUserGroupDialog() {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)

  const currentOrganization = useAppSelector((state) => state.organization.currentOrganization)

  const dispatch = useAppDispatch()

  const initialValues: ICreateUserGroup = {
    organizationId: "",
    name: "",
  }

  const createOrganizationValidationScheme = Yup.object({
    name: Yup.string()
      .required("Please enter organization name")
      .max(64, "Maximum length is 64 characters"),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: createOrganizationValidationScheme,
    onSubmit: (values) => {
      if (currentOrganization === undefined) {
        return
      }

      setIsLoading(true)

      const newUserGroup: ICreateUserGroup = {
        name: values.name,
        organizationId: currentOrganization.id,
      }

      userGroupService
        .createUserGroup(newUserGroup)
        .then((resp) => {
          dispatch(addUserGroup(resp))
          setIsLoading(false)
          handleClose()
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div>
      <Button data-testid="createUserGroupButton" variant="contained" onClick={handleClickOpen}>
        create USER group
      </Button>
      <Dialog open={open} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>Create User Group</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="User Group Name"
              type="name"
              fullWidth
              variant="outlined"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading}>
                Create
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
